/* Video */
const SUPPORTED_VIDEO_FORMATS = [
  "webm",
  "mp4",
  "m4v",
  "ogg",
  "ogv",
  "mov",
] as const

export type VideoFormat = (typeof SUPPORTED_VIDEO_FORMATS)[number]

export type VideoMedia = {
  type: "video"
  format: VideoFormat
}

const getVideoFormat = (extension: string) => {
  return getSupportedType(extension, SUPPORTED_VIDEO_FORMATS)
}

/* Audio */
const SUPPORTED_AUDIO_FORMATS = ["mp3", "wav"] as const

type Audio = {
  type: "audio"
  format: (typeof SUPPORTED_AUDIO_FORMATS)[number]
}

const getAudioFormat = (extension: string) => {
  return getSupportedType(extension, SUPPORTED_AUDIO_FORMATS)
}

/* 3D Models */
const SUPPORTED_3D_MODEL_FORMATS = ["gltf", "glb"] as const

type ThreeD = {
  type: "3d"
  format: (typeof SUPPORTED_3D_MODEL_FORMATS)[number]
}

const get3dFormat = (extension: string) => {
  return getSupportedType(extension, SUPPORTED_3D_MODEL_FORMATS)
}

/* HTML */
type Html = { type: "html" }

/* Image */
const SUPPORTED_IMAGE_FORMATS = [
  "png",
  "jpeg",
  "jpg",
  "bmp",
  "avif",
  "webp",
  "gif",
  "svg",
]

type Image = {
  type: "image"
  format: (typeof SUPPORTED_IMAGE_FORMATS)[number]
}

const getImageFormat = (extension: string) => {
  return getSupportedType(extension, SUPPORTED_IMAGE_FORMATS)
}

type MediaVariant = VideoMedia | Audio | ThreeD | Html | Image

export function extractMediaVariant(uri: string): MediaVariant | undefined {
  let url: URL
  try {
    url = new URL(uri)
  } catch {
    return undefined
  }
  const extension = url.pathname.split(".").pop()
  if (!extension) {
    return undefined
  }
  if (extension === "html") {
    return { type: "html" }
  }
  const video = getVideoFormat(extension)
  if (video) {
    return { type: "video", format: video }
  }
  const audio = getAudioFormat(extension)
  if (audio) {
    return { type: "audio", format: audio }
  }
  const threed = get3dFormat(extension)
  if (threed) {
    return { type: "3d", format: threed }
  }
  let image = getImageFormat(extension)
  if (image) {
    if (image === "jpg") {
      image = "jpeg"
    }
    return { type: "image", format: image }
  }
  return undefined
}

export const isVideoUrl = (uri: string): boolean => {
  return extractMediaVariant(uri)?.type === "video"
}

const getSupportedType = <T extends readonly string[]>(
  extension: string,
  supportedTypes: T,
): T[number] | undefined => {
  if (!extension) {
    return undefined
  }
  return supportedTypes.find(
    supportedExtension => supportedExtension === extension.toLowerCase(),
  )
}
